import React, { useState, useMemo } from 'react';
import { Button, InputNumber, Modal, Card, Col, Row } from 'antd';
import { useDispatch, useSelector } from 'react-redux';
import { selectors, breakDeposit, DepositInstrument, makeDeposit } from 'state';
import { formatters } from 'services';
import {
  eodPriceSegments,
  selectedPerson,
} from '../../../../state/app/selectors';
import { AreaChart } from 'components';
import AfsEodPriceYieldChart from './AfsEodPriceYieldChart';
// import React, { useMemo } from 'react';
// import { Button, Modal } from 'antd';
// import { useSelector } from 'react-redux';
// import { selectors } from 'state';

type Props = { visible: boolean; hide: () => void };

const AssetEodPreviewModal: React.FC<Props> = ({ visible, hide }) => {
  const assetEodData = useSelector(selectors.assetEodData);
  const segmentsRawData = useSelector(selectors.eodPriceSegments);
  const segmentsDataSource = segmentsRawData.map((segment, i) => ({
    data: segment,
    name: `${i + 1}`,
  }));
  const supportsYieldPreview =
    assetEodData.supports_yield_preview || assetEodData.is_afs;

  const bid_series = useMemo(
    () =>
      assetEodData.is_yield || supportsYieldPreview
        ? []
        : assetEodData.data.map((d: any) => ({ x: d.timestamp, y: d.bid })),
    [assetEodData, supportsYieldPreview],
  );
  const ask_series = useMemo(
    () =>
      assetEodData.is_yield || supportsYieldPreview
        ? []
        : assetEodData.data.map((d: any) => ({ x: d.timestamp, y: d.ask })),
    [assetEodData, supportsYieldPreview],
  );
  const close_series = useMemo(
    () =>
      assetEodData.is_yield || supportsYieldPreview
        ? []
        : assetEodData.data.map((d: any) => ({ x: d.timestamp, y: d.close })),
    [assetEodData, supportsYieldPreview],
  );
  const yield_series = useMemo(
    () =>
      assetEodData.is_yield
        ? assetEodData.data.map((d: any) => ({
            x: d.timestamp,
            y: d.yield_value,
          }))
        : [],
    [assetEodData],
  );

  const toIso = (t: string | number) =>
    typeof t === 'number'
      ? new Date(t).toISOString()
      : new Date(t).toISOString?.() || String(t);

  const onDownloadCsv = () => {
    const rows = assetEodData.is_yield
      ? assetEodData.data.map((d: any) => [toIso(d.timestamp), d.yield_value])
      : supportsYieldPreview
        ? assetEodData.data.map((d: any) => [
            toIso(d.timestamp),
            d.bid,
            d.ask,
            d.close,
            d.bid_yield,
            d.ask_yield,
          ])
        : assetEodData.data.map((d: any) => [
            toIso(d.timestamp),
            d.bid,
            d.ask,
            d.close,
          ]);

    const header = assetEodData.is_yield
      ? ['timestamp', 'yield']
      : supportsYieldPreview
        ? ['timestamp', 'bid', 'ask', 'close', 'bid_yield', 'ask_yield']
        : ['timestamp', 'bid', 'ask', 'close'];

    const csv =
      header.join(',') +
      '\n' +
      rows
        .map(r =>
          r
            .map(v =>
              v === null || v === undefined
                ? ''
                : /[",\n]/.test(String(v))
                  ? `"${String(v).replace(/"/g, '""')}"`
                  : String(v),
            )
            .join(','),
        )
        .join('\n');

    const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `${assetEodData.ric}_${
      assetEodData.is_yield
        ? 'yield'
        : supportsYieldPreview
          ? 'prices_and_yields'
          : 'prices'
    }_eod.csv`;
    document.body.appendChild(a);
    a.click();
    a.remove();
    URL.revokeObjectURL(url);
  };

  return (
    <Modal
      // open={visible}
      open={visible}
      onCancel={hide}
      title={`All EoD datapoints for: ${assetEodData.ric}`}
      footer={
        <Button type="primary" onClick={onDownloadCsv}>
          Download CSV
        </Button>
      }
    >
      <div>
        {assetEodData.is_yield ? (
          <AreaChart
            title=""
            dataSeriesName="yield"
            data={yield_series}
            yaxisFormatter={formatters.commas4Digits}
          />
        ) : supportsYieldPreview ? (
          <AfsEodPriceYieldChart data={assetEodData.data} />
        ) : (
          <AreaChart
            title=""
            dataSeriesName="bid"
            data={bid_series}
            yaxisFormatter={formatters.commas4Digits}
            additionalLines={[
              { data: close_series, name: 'close' },
              { data: ask_series, name: 'ask' },
            ]}
          />
        )}
        <AreaChart
          title={'Segments'}
          dataSeriesName={'price'}
          data={[]}
          additionalLines={segmentsDataSource}
        />
      </div>
    </Modal>
  );
};

export default AssetEodPreviewModal;
